import { ProviderError } from './errors';
/**
 * Provider identifier for the Guest device adapter. Consumers can
 * compare against this const rather than the literal string when
 * inspecting a `ProviderFileRef.provider`.
 */
export const DEVICE_PROVIDER_ID = 'device-guest';
/**
 * Guest-mode storage adapter for the local device. Uses the File
 * System Access API (`showOpenFilePicker` + `FileHandle.createWritable`)
 * on Chromium/Edge, and falls back to `<input type=file>` for open +
 * `URL.createObjectURL` + programmatic anchor click for save on
 * Firefox/Safari/iOS Safari.
 *
 * Ref shape (`opaque` field of `ProviderFileRef`): a
 * `DeviceRefPayload` object holding the `File` and optionally the
 * `FileSystemFileHandle`. Consumers must not construct refs manually;
 * use `pick()` or the `wrapFile()` helper.
 *
 * Anchored to
 * [ADR-006](../../../docs/adr/ADR-006-provider-adapter-pattern.md)
 * and [C-01 §4.3](../../../docs/phase-c-information-systems-architecture/application-architecture.md).
 */
export class DeviceStorageProvider {
    /**
     * Wrap a `File` (e.g., from a drag-and-drop handler) into a
     * `ProviderFileRef`. NOT part of `IStorageProvider`; provided so
     * consumers using non-picker entry points (drop-zone, paste, etc.)
     * can obtain a valid ref without going through `pick()`.
     *
     * The resulting ref has no `FileSystemFileHandle`, so a subsequent
     * `upload(ref, bytes)` falls through to Blob download rather than
     * in-place write.
     */
    wrapFile(file) {
        const payload = { file };
        return { provider: DEVICE_PROVIDER_ID, opaque: payload };
    }
    async pick(mimeFilter) {
        const picker = globalThis.showOpenFilePicker;
        if (typeof picker === 'function') {
            return await this.pickViaFsAccessApi(picker, mimeFilter);
        }
        return await this.pickViaFileInput(mimeFilter);
    }
    async pickViaFsAccessApi(picker, mimeFilter) {
        const types = buildFsAccessTypes(mimeFilter);
        let handle;
        try {
            const result = await picker({
                types,
                multiple: false,
                excludeAcceptAllOption: false,
            });
            const first = result[0];
            if (!first) {
                return null;
            }
            handle = first;
        }
        catch (err) {
            // AbortError = user cancelled the dialog. Idiomatic: not an error.
            if (err instanceof DOMException && err.name === 'AbortError') {
                return null;
            }
            throw new ProviderError(err instanceof Error ? err.message : 'pick failed', {
                code: 'pick-failed',
                cause: err,
            });
        }
        let file;
        try {
            file = await handle.getFile();
        }
        catch (err) {
            // Race: the picked file was moved/deleted between picker and
            // getFile. Wrap for hierarchy consistency with the picker error.
            throw new ProviderError(err instanceof Error ? err.message : 'getFile failed', {
                code: 'pick-failed',
                cause: err,
            });
        }
        validateMime(file, mimeFilter);
        const payload = { handle, file };
        return { provider: DEVICE_PROVIDER_ID, opaque: payload };
    }
    pickViaFileInput(mimeFilter) {
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = false;
            if (mimeFilter?.mimeTypes.length) {
                // Include both MIME types AND file extensions in `accept`.
                // Safari filters aggressively on MIME alone; some PDFs report
                // `application/x-pdf` or an empty type. Extensions widen the
                // picker's default view without sacrificing safety
                // (validateMime still enforces the MIME allow-list on the
                // resulting File).
                const parts = [...mimeFilter.mimeTypes, ...(mimeFilter.extensions ?? [])];
                input.accept = parts.join(',');
            }
            input.addEventListener('change', () => {
                const file = input.files?.[0];
                if (!file) {
                    resolve(null);
                    return;
                }
                try {
                    validateMime(file, mimeFilter);
                }
                catch (err) {
                    reject(err);
                    return;
                }
                const payload = { file };
                resolve({ provider: DEVICE_PROVIDER_ID, opaque: payload });
            }, { once: true });
            // A cancelled picker fires no event on some browsers, so this
            // promise may never resolve on cancel. Guest-mode `pick` is
            // always called in response to a user gesture; the leaked
            // Promise is scoped to that gesture and cleaned up on unmount.
            input.click();
        });
    }
    async download(ref) {
        const payload = this.mustGetPayload(ref);
        const buffer = await payload.file.arrayBuffer();
        return {
            bytes: new Uint8Array(buffer),
            meta: metaFromFile(payload.file),
        };
    }
    async upload(ref, bytes) {
        const payload = this.mustGetPayload(ref);
        if (payload.handle) {
            try {
                const writable = await payload.handle.createWritable();
                await writable.write(toBlobPart(bytes));
                await writable.close();
                return { mode: 'in-place' };
            }
            catch (err) {
                throw new ProviderError(err instanceof Error ? err.message : 'in-place write failed', {
                    code: 'write-failed',
                    cause: err,
                });
            }
        }
        // No handle (fallback path or `wrapFile` origin) — degrade to Blob
        // download with the original filename so the user still gets the
        // edited bytes. Return `mode: 'download'` so the caller can pick
        // the right UI (e.g., "Downloaded" toast vs "Saved" toast).
        await saveAsBlob(bytes, payload.file.name, payload.file.type || undefined);
        return { mode: 'download' };
    }
    async initResumable(_size, _opts) {
        throw new ProviderError('Device provider does not support resumable uploads; use `upload` or `saveAs`.', { code: 'unsupported' });
    }
    /**
     * Triggers a browser Blob download so the user can save to disk.
     *
     * ⚠️ Semantic note: the returned `ProviderFileRef` represents
     * **intent to save**, NOT confirmed save. We cannot observe whether
     * the user accepts or cancels the browser's native save dialog —
     * `URL.createObjectURL` + anchor-click hands off asynchronously and
     * the browser gives us no completion signal. The returned ref
     * wraps a synthesized in-memory `File` so subsequent
     * `download(ref)` / `getFileMeta(ref)` round-trip the bytes we
     * TRIED to save, not necessarily what is on disk.
     *
     * Callers that need a confirmed-save signal (e.g., a compliance
     * audit event) must obtain it out-of-band; the web platform does
     * not expose one at this API surface.
     */
    async saveAs(bytes, name) {
        // Copy the bytes once, then reuse for both the Blob download and
        // the synthesized File. Without this, `saveAsBlob(bytes, ...)`
        // would call `toBlobPart(bytes)` internally and we'd copy again
        // for `new File([...])` — 2× the memory on large PDFs.
        const buf = toBlobPart(bytes);
        await saveAsBlobFromBuffer(buf, name, 'application/pdf');
        const file = new File([buf], name, { type: 'application/pdf' });
        return this.wrapFile(file);
    }
    async getFileMeta(ref) {
        return metaFromFile(this.mustGetPayload(ref).file);
    }
    mustGetPayload(ref) {
        if (ref.provider !== DEVICE_PROVIDER_ID) {
            throw new ProviderError(`Not a device ref: provider=${String(ref.provider)}`, {
                code: 'invalid-ref',
            });
        }
        const payload = ref.opaque;
        if (!isDeviceRefPayload(payload)) {
            throw new ProviderError('opaque must be a DeviceRefPayload with a File', {
                code: 'invalid-ref',
            });
        }
        return payload;
    }
}
// ── Module-scope helpers ──────────────────────────────────────────────
// TypeScript 5.7+ tightened `Uint8Array` to `Uint8Array<ArrayBufferLike>`
// where `ArrayBufferLike = ArrayBuffer | SharedArrayBuffer`. `Blob`,
// `File`, and `FileSystemWritableFileStream.write` all want the
// non-shared `Uint8Array<ArrayBuffer>` variant. `bytes.slice()` returns
// a fresh view backed by a plain `ArrayBuffer` — one small copy in
// exchange for a clean type.
function toBlobPart(bytes) {
    return bytes.slice();
}
function buildFsAccessTypes(mimeFilter) {
    if (!mimeFilter?.mimeTypes.length) {
        return undefined;
    }
    const accept = {};
    for (const mime of mimeFilter.mimeTypes) {
        accept[mime] = mimeFilter.extensions ?? [];
    }
    return [
        {
            description: mimeFilter.description ?? 'Files',
            accept,
        },
    ];
}
function validateMime(file, mimeFilter) {
    if (!mimeFilter?.mimeTypes.length) {
        return;
    }
    if (file.type && mimeFilter.mimeTypes.includes(file.type)) {
        return;
    }
    // Some browsers (e.g., older Safari on drop) omit `File.type` for
    // certain PDFs. If the mime allow-list is exactly `application/pdf`
    // and the file has no type, treat it as best-effort acceptable —
    // callers can inspect bytes downstream if strict verification is
    // required.
    if (!file.type &&
        mimeFilter.mimeTypes.length === 1 &&
        mimeFilter.mimeTypes[0] === 'application/pdf') {
        return;
    }
    throw new ProviderError(`File type "${file.type || 'unknown'}" not allowed. Expected one of: ${mimeFilter.mimeTypes.join(', ')}`, { code: 'invalid-mime' });
}
function metaFromFile(file) {
    return {
        name: file.name,
        size: file.size,
        mtime: new Date(file.lastModified),
        mime: file.type || undefined,
    };
}
async function saveAsBlob(bytes, name, mime) {
    await saveAsBlobFromBuffer(toBlobPart(bytes), name, mime);
}
async function saveAsBlobFromBuffer(buf, name, mime) {
    const blob = new Blob([buf], mime ? { type: mime } : {});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // ⚠️ Do NOT revoke synchronously: `a.click()` schedules the
    // download asynchronously; the browser reads the blob URL on the
    // next tick. Synchronous revoke races the read and can produce a
    // 0-byte download on some browsers (older Safari, certain Firefox
    // versions). 250 ms is generous but well under the "user notices"
    // threshold and matches the pattern used by mature web-download
    // libraries (StreamSaver.js, file-saver).
    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 250);
}
function isDeviceRefPayload(v) {
    return typeof v === 'object' && v !== null && 'file' in v && v.file instanceof File;
}
//# sourceMappingURL=DeviceStorageProvider.js.map